import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { NewBadge, StarButton } from "@/components/CareerActions";
import { useIdList, COMPARE_KEY } from "@/lib/idList";

const ROWS = [
  ["Field", (c) => c.cluster_label], ["Typical pay (US, approx.)", (c) => c.pay], ["Training path", (c) => c.training],
  ["Certification / licensing", (c) => c.certifications], ["Work environment", (c) => c.environment], ["Indoor / outdoor", (c) => c.labels.setting],
  ["Remote work", (c) => c.labels.remote], ["Physical demand", (c) => c.labels.physical], ["People around you", (c) => c.labels.people],
  ["Deals with the public", (c) => c.labels.public], ["Supplies, gear & clothing", (c) => c.gear], ["Realistic expectations", (c) => c.expectations],
];

export default function CompareCareers() {
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const { ids: picked, clear } = useIdList(COMPARE_KEY, 3);
  const ids = sp.get("ids") || picked.join(",");
  useEffect(() => {
    http.get("/careers/compare", { params: { ids } }).then((r) => setData(r.data.careers)).catch((e) => setError(e.response?.data?.detail || "Could not load"));
  }, [ids]);

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-10" data-testid="compare-page">
      <Link to="/careers" data-testid="compare-back-link" className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-[#1C242B]"><ArrowLeft size={16} /> All careers</Link>
      <p className="overline-label mt-6">Compare careers</p>
      <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mt-3">Side by side</h1>
      {error && <p className="mt-8 text-slate-600" data-testid="compare-error">{error}</p>}
      {!data && !error && <Loader2 className="mt-10 animate-spin text-[#D96B43]" />}
      {data && (
        <div className="mt-8 overflow-x-auto rounded-2xl border border-[#1C242B]/10 bg-white">
          <table className="w-full min-w-[720px] text-sm" data-testid="compare-table">
            <thead>
              <tr className="border-b border-[#1C242B]/10">
                <th className="w-48" />
                {data.map((c) => (
                  <th key={c.id} className="text-start align-top p-5" data-testid={`compare-col-${c.id}`}>
                    <div className="flex items-center gap-2">{c.is_new && <NewBadge testId={`compare-new-${c.id}`} />}<StarButton id={c.id} testId={`compare-star-${c.id}`} /></div>
                    <Link to={`/careers/${c.id}`} className="mt-1 block font-display font-bold text-lg text-[#1C242B] hover:text-[#D96B43] transition-colors">{c.title}</Link>
                    <p className="mt-1 font-normal text-slate-500">{c.summary}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ROWS.map(([label, get]) => (
                <tr key={label} className="border-b border-[#1C242B]/5 last:border-0 align-top">
                  <th className="text-start p-5 font-mono-ui text-[11px] uppercase tracking-wider text-slate-500 bg-[#FAF8F5]">{label}</th>
                  {data.map((c) => <td key={c.id} className="p-5 text-[#1C242B] leading-relaxed">{get(c)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-8 flex flex-wrap gap-3">
        <button onClick={clear} data-testid="compare-clear-btn" className="rounded-full border border-[#1C242B]/15 px-5 py-2.5 text-sm hover:bg-[#1C242B]/5 transition-colors">Clear comparison</button>
        <Link to="/assessment" data-testid="compare-take-test-btn" className="rounded-full bg-[#D96B43] text-white px-5 py-2.5 text-sm font-semibold hover:bg-[#C05630] transition-colors">Take the test to see your fit</Link>
      </div>
    </div>
  );
}
